"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts, Term } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsList() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col gap-[120px]">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col items-start animate-pulse">
            <div className="h-10 w-3/4 bg-muted mb-6" />
            <div className="flex gap-4 mb-5">
              <div className="h-5 w-24 bg-muted" />
              <div className="h-5 w-32 bg-muted" />
            </div>
            <div className="h-4 w-full bg-muted mb-2" />
            <div className="h-4 w-2/3 bg-muted mb-8" />
            <div className="h-4 w-24 bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground font-sans">
        Essays konnten nicht geladen werden.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground font-sans">
        Noch keine Essays veröffentlicht.
      </div>;
  }
  return <div className="flex flex-col gap-[120px]">
      {posts.map((post, i) => {
      const readingTime = post.customFields?.reading_time || "5";
      const formattedDate = post.date ? new Date(post.date).toLocaleDateString('de-CH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) : "Kürzlich";
      return <article key={post.id} data-index={i} className="flex flex-col items-start group/article">
            <Link to={post.link || "#"} className="block w-full">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-foreground transition-colors duration-500 group-hover/article:text-ring">
                {post.title}
              </h2>
            </Link>
            
            <div className="flex items-center gap-4 mt-6 mb-5">
              <Badge variant="outline" className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground border-border/50 bg-transparent">
                {readingTime} Min Lesezeit
              </Badge>
              <span className="font-sans text-[11px] uppercase tracking-widest text-muted-foreground">
                {formattedDate}
              </span>
            </div>

            {post.excerpt && <div className="font-sans text-base md:text-lg leading-relaxed text-foreground/80 line-clamp-2 max-w-2xl" dangerouslySetInnerHTML={{
          __html: post.excerpt
        }} />}

            <Link to={post.link || "#"} className="inline-block mt-8 font-sans text-xs uppercase tracking-widest font-medium text-foreground relative after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-foreground after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100">
              Essay lesen
            </Link>
          </article>;
    })}
    </div>;
}
function PostsContent() {
  const {
    posts,
    setQuery
  } = usePosts();
  const [availableTags, setAvailableTags] = useState<Term[]>([]);
  const [activeTag, setActiveTag] = useState<number | null>(null);
  const [availableCategories, setAvailableCategories] = useState<Term[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  useEffect(() => {
    if (posts.length > 0) {
      if (availableTags.length === 0) {
        const tagsMap = new Map<number, Term>();
        posts.forEach(post => {
          post.tags?.forEach(tag => {
            tagsMap.set(tag.id, tag);
          });
        });
        if (tagsMap.size > 0) setAvailableTags(Array.from(tagsMap.values()));
      }
      if (availableCategories.length === 0) {
        const catsMap = new Map<number, Term>();
        posts.forEach(post => {
          post.categories?.forEach(cat => {
            catsMap.set(cat.id, cat);
          });
        });
        if (catsMap.size > 0) setAvailableCategories(Array.from(catsMap.values()));
      }
    }
  }, [posts, availableTags.length, availableCategories.length]);
  const handleTagClick = (tagId: number | null) => {
    setActiveTag(tagId);
    setQuery({
      tags: tagId ? String(tagId) : undefined,
      categories: activeCategory ? String(activeCategory) : undefined
    });
  };
  const handleCategoryClick = (catId: number | null) => {
    setActiveCategory(catId);
    setQuery({
      categories: catId ? String(catId) : undefined,
      tags: activeTag ? String(activeTag) : undefined
    });
  };
  return <div className="flex flex-col gap-20">
      <div className="space-y-12">
        {availableCategories.length > 0 && <div className="flex flex-col items-center gap-4">
            <span className="font-sans text-[10px] font-medium tracking-widest uppercase text-muted-foreground">
              Kategorien
            </span>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
              <Button variant="ghost" onClick={() => handleCategoryClick(null)} className={`h-auto rounded-none border-b px-0 py-1 font-sans text-sm transition-colors duration-200 hover:bg-transparent ${activeCategory === null ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                Alle Kategorien
              </Button>
              {availableCategories.map((cat, i) => <Button key={cat.id} data-index={i} variant="ghost" onClick={() => handleCategoryClick(cat.id)} className={`h-auto rounded-none border-b px-0 py-1 font-sans text-sm transition-colors duration-200 hover:bg-transparent ${activeCategory === cat.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {cat.name}
                </Button>)}
            </div>
          </div>}

        {availableTags.length > 0 && <div className="flex flex-col items-center gap-4">
            <span className="font-sans text-[10px] font-medium tracking-widest uppercase text-muted-foreground">
              Themen
            </span>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
              <Button variant="ghost" onClick={() => handleTagClick(null)} className={`h-auto rounded-none border-b px-0 py-1 font-sans text-sm transition-colors duration-200 hover:bg-transparent ${activeTag === null ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                Alle Themen
              </Button>
              {availableTags.map((tag, i) => <Button key={tag.id} data-index={i} variant="ghost" onClick={() => handleTagClick(tag.id)} className={`h-auto rounded-none border-b px-0 py-1 font-sans text-sm transition-colors duration-200 hover:bg-transparent ${activeTag === tag.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {tag.name}
                </Button>)}
            </div>
          </div>}
      </div>

      <PostsList />
    </div>;
}
export default function Posts() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="posts" className="bg-background py-24 md:py-32 lg:py-40">
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <PostsContent />
        </PostsProvider>
      </div>
    </section>;
}